import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, addDoc, deleteDoc } from 'firebase/firestore';
import { ref as sRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../services/imageOptimizer';
import { Camera, Trash2, Loader2, Image as ImageIcon, Plus } from 'lucide-react';

interface AcademyLifeItem {
    id: string;
    imageUrl: string;
    title?: string;
    desc?: string;
    caption?: string;
    createdAt: number;
}

const AcademyLifeRoot: React.FC = () => {
    const [images, setImages] = useState<AcademyLifeItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const q = query(collection(db, 'academy-life'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: AcademyLifeItem[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as AcademyLifeItem);
            });
            setImages(list);
        });
        return () => unsubscribe();
    }, []);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check if file is image
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드할 수 있습니다.');
            return;
        }

        setUploading(true);
        try {
            const fileName = `${Date.now()}_${file.name.replace(/\.[^/.]+$/, "")}.webp`;
            const storageRef = sRef(storage, `academy-life/${fileName}`);
            
            // Upload to storage with optimization
            const snapshot = await uploadOptimizedImage(storageRef, file, { maxWidth: 1600, quality: 0.85 });
            const downloadUrl = await getDownloadURL(snapshot.ref);

            // Save document in Firestore
            await addDoc(collection(db, 'academy-life'), {
                imageUrl: downloadUrl,
                title: title.trim() || file.name.split('.')[0],
                desc: desc.trim(),
                createdAt: Date.now()
            });

            setTitle('');
            setDesc('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            alert('이미지가 성공적으로 등록되었습니다.');
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('이미지 업로드에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (item: AcademyLifeItem) => {
        if (!confirm('해당 이미지를 학원생활 갤러리에서 삭제하시겠습니까?')) return;

        try {
            // Delete from Firestore
            await deleteDoc(doc(db, 'academy-life', item.id));

            // Delete from Storage
            try {
                const storageRef = sRef(storage, item.imageUrl);
                await deleteObject(storageRef);
            } catch (storageErr) {
                // If storage deletion fails (e.g. file doesn't exist anymore), ignore and proceed
                console.warn('Storage deletion failed or skipped:', storageErr);
            }

            alert('이미지가 삭제되었습니다.');
        } catch (error) {
            console.error('Error deleting image:', error);
            alert('이미지 삭제 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b border-slate-100 px-8 py-6 flex items-center justify-between shrink-0">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
                        <Camera className="text-indigo-600" size={24} />
                        학원생활 이미지 관리
                    </h1>
                    <p className="text-slate-500 font-medium text-xs md:text-sm">
                        랜딩 페이지의 학원생활 갤러리에 노출되는 활동사진을 관리합니다.
                    </p>
                </div>
            </div>

            {/* Content Container */}
            <div className="p-8 space-y-8 flex-1 max-w-7xl mx-auto w-full">
                {/* Upload Form Area */}
                <div className="bg-white rounded-[2rem] border border-slate-100 p-6 md:p-8 shadow-sm flex flex-col md:flex-row gap-6 items-stretch">
                    <div className="flex-1 space-y-4">
                        <h3 className="font-black text-slate-800 text-lg">새 이미지 등록</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">
                                    사진 제목
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                    placeholder="사진의 제목을 입력하세요."
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    disabled={uploading}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">
                                    사진 설명 (선택)
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                    placeholder="사진에 대한 간단한 설명을 입력하세요."
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                    disabled={uploading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full md:w-80 flex flex-col justify-end">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            accept="image/*"
                            disabled={uploading}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="h-[52px] w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 active:scale-98"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    업로드 중...
                                </>
                            ) : (
                                <>
                                    <Plus size={18} />
                                    사진 파일 선택하기
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Gallery List */}
                <div className="space-y-6">
                    <h3 className="font-black text-slate-800 text-lg flex items-center gap-2">
                        <ImageIcon className="text-slate-400" size={20} />
                        등록된 이미지 목록 ({images.length})
                    </h3>

                    {images.length === 0 ? (
                        <div className="bg-white rounded-[2rem] border border-slate-100 py-20 text-center text-slate-400 shadow-sm">
                            <ImageIcon size={48} className="mx-auto text-slate-300 mb-4" />
                            <p className="font-bold">등록된 학원생활 사진이 없습니다.</p>
                            <p className="text-xs text-slate-400 font-medium mt-1">위의 폼을 통해 새로운 활동사진을 등록해보세요.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {images.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col h-full"
                                >
                                    <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden shrink-0">
                                        <img
                                            src={item.imageUrl}
                                            alt={item.title || item.caption}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(item)}
                                                className="w-10 h-10 rounded-xl bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105 active:scale-95"
                                                title="삭제"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col justify-between gap-2 text-left">
                                        <div className="space-y-1">
                                            <p className="font-black text-slate-800 text-sm line-clamp-1">
                                                {item.title || item.caption || '정성우학원 활동 모습'}
                                            </p>
                                            <p className="text-xs text-slate-500 font-medium line-clamp-2">
                                                {item.desc || (item.title ? '' : '설명 없음')}
                                            </p>
                                        </div>
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AcademyLifeRoot;
